import { formatRecordDate } from "@taunggyi-plus/design-system";
import { RecordFormDialog } from "@taunggyi-plus/design-system";
import { useFeedback } from "@taunggyi-plus/auth";
import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { useAuth } from "@taunggyi-plus/auth";
import {
  Card,
  DetailCard,
  ManagementListToolbar,
  PaginatedList,
  StatusPill,
} from "@taunggyi-plus/design-system";

type Organization = {
  id: string;
  name: string;
  status: string | null;
  fee_minor: number | null;
  renews_at: string | null;
  billing_cycle: string | null;
};
type Request = {
  id: string;
  organization_id: string;
  fee_minor: number;
  billing_cycle: string;
  payment_reference: string;
  status: string;
  review_note: string;
  created_at: string;
};
const money = (amount: number) => `${(amount / 100).toLocaleString()} MMK`;

export function TaxiSubscriptionRequests({ admin = false }: { readonly admin?: boolean }) {
  const auth = useAuth();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [requests, setRequests] = useState<Request[]>([]);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [orgId, setOrgId] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useFeedback();
  const [review, setReview] = useState<{
    request: Request;
    decision: "approved" | "rejected";
  } | null>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  const load = useCallback(async () => {
    if (!auth.client) return;
    const { data, error } = await auth.client.rpc("taxi_subscription_requests_list", {});
    if (error) throw new Error(error.message);
    const result = data as { organizations: Organization[]; requests: Request[] };
    setOrganizations(result.organizations);
    setRequests(result.requests);
    setOrgId((current) => current || result.organizations[0]?.id || "");
    setLoaded(true);
  }, [auth.client]);
  useEffect(() => {
    const refresh = () => {
      void load().catch(() =>
        setMessage("Could not load subscription requests. Please try again."),
      );
    };
    const first = window.setTimeout(refresh, 0);
    const timer = window.setInterval(() => {
      if (!document.hidden) refresh();
    }, 30_000);
    return () => {
      window.clearTimeout(first);
      window.clearInterval(timer);
    };
  }, [load, setMessage]);
  const filtered = requests
    .filter(
      (r) =>
        (status === "all" || r.status === status) &&
        `${organizations.find((o) => o.id === r.organization_id)?.name ?? ""} ${r.payment_reference} ${r.review_note}`
          .toLowerCase()
          .includes(query.trim().toLowerCase()),
    )
    .sort((a, b) => Number(b.status === "pending") - Number(a.status === "pending"));
  const organization = organizations.find((org) => org.id === orgId);
  const pending = requests.some(
    (request) => request.organization_id === orgId && request.status === "pending",
  );
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!auth.client || busy) return;
    const form = event.currentTarget;
    const values = new FormData(form);
    const fee = organization?.fee_minor;
    const cycle = organization?.billing_cycle;
    if (
      !fee ||
      !Number.isSafeInteger(fee) ||
      fee <= 0 ||
      !["monthly", "annual"].includes(cycle ?? "")
    ) {
      setMessage("Ask admin to configure your subscription fee and billing cycle first.");
      return;
    }
    setBusy(true);
    setMessage("");
    try {
      const { error } = await auth.client.rpc("submit_taxi_subscription_request", {
        target_organization_id: orgId,
        target_fee_minor: fee,
        target_billing_cycle: cycle,
        payment_reference: String(values.get("reference")).trim(),
      });
      if (error) throw new Error(error.message);
      form.reset();
      setMessage("Subscription fee submitted. Waiting for admin approval.");
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to submit request.");
    } finally {
      setBusy(false);
    }
  }
  async function decide(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!auth.client || !review || busy) return;
    const values = new FormData(event.currentTarget);
    setBusy(true);
    setMessage("");
    try {
      const { error } = await auth.client.rpc("review_taxi_subscription_request", {
        target_request_id: review.request.id,
        decision: review.decision,
        note: String(values.get("note") || "").trim(),
      });
      if (error) throw new Error(error.message);
      dialog.current?.close();
      setReview(null);
      setMessage(
        review.decision === "approved"
          ? "Approved. Subscription renewed and payment receipt recorded in Finance."
          : "Request rejected. The reason is visible in the provider app.",
      );
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to review request.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="taxi-subscriptions">
      {!admin && (
        <a href="/more" className="more-back-link">
          ← More
        </a>
      )}

      {message && <p role="status">{message}</p>}
      {!loaded && !message && <p role="status">Loading subscriptions…</p>}
      {!admin &&
        loaded &&
        (organizations.length ? (
          <Card className="taxi-subscription-card">
            <label>
              Taxi service
              <select value={orgId} onChange={(event) => setOrgId(event.target.value)}>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            </label>
            <p>
              Subscription: <strong>{organization?.status ?? "Not set"}</strong>
              {organization?.fee_minor
                ? ` · ${money(organization.fee_minor)} / ${organization.billing_cycle}`
                : ""}
              {organization?.renews_at ? ` · Renews ${organization.renews_at}` : ""}
            </p>
            {pending ? (
              <p role="status">Your subscription fee is waiting for admin approval.</p>
            ) : !organization?.fee_minor ||
              !["monthly", "annual"].includes(organization.billing_cycle ?? "") ? (
              <p role="status">
                Ask admin to configure your subscription fee and billing cycle first.
              </p>
            ) : (
              <RecordFormDialog title="Submit subscription payment">
                <form onSubmit={submit} className="taxi-subscription-form">
                  <label>
                    Fee amount (MMK)
                    <input name="fee" type="number" value={organization.fee_minor / 100} readOnly />
                  </label>
                  <label>
                    Billing cycle
                    <input
                      name="cycle"
                      value={organization.billing_cycle === "annual" ? "Annual" : "Monthly"}
                      readOnly
                    />
                  </label>
                  <label>
                    Payment reference or receipt number
                    <input name="reference" maxLength={240} required pattern=".*\S.*" />
                  </label>
                  <button className="button button--primary" disabled={busy}>
                    {busy ? "Submitting…" : "Submit for approval"}
                  </button>
                </form>
              </RecordFormDialog>
            )}
          </Card>
        ) : (
          <p>No taxi service is linked to your account. Contact support to link your membership.</p>
        ))}
      <h3>{admin ? "Requests" : "Request history"}</h3>
      {loaded && requests.length === 0 && <p>No subscription requests yet.</p>}
      <ManagementListToolbar
        query={query}
        onQueryChange={setQuery}
        searchLabel="Search subscription requests"
        resultCount={filtered.length}
        filters={[
          {
            label: "Status",
            value: status,
            onChange: setStatus,
            options: ["all", "pending", "approved", "rejected"].map((value) => ({
              value,
              label: value === "all" ? "All statuses" : value,
            })),
          },
        ]}
        onClear={() => {
          setQuery("");
          setStatus("all");
        }}
      />
      {loaded && !filtered.length && requests.length > 0 && <p>No requests match these filters.</p>}
      <div className="taxi-subscription-list">
        <PaginatedList
          items={filtered}
          resetKey={`${query}:${status}`}
          label="Subscription request pages"
          renderItem={(request) => (
            <DetailCard
              key={request.id}
              title={
                organizations.find((org) => org.id === request.organization_id)?.name ??
                "Taxi service"
              }
              summary={<StatusPill>{request.status}</StatusPill>}
              className="taxi-subscription-card"
            >
              <strong>
                {organizations.find((org) => org.id === request.organization_id)?.name ??
                  "Taxi service"}
              </strong>
              <p>
                {money(request.fee_minor)} · {request.billing_cycle} ·{" "}
                <StatusPill
                  tone={
                    request.status === "approved"
                      ? "success"
                      : request.status === "pending"
                        ? "warning"
                        : "neutral"
                  }
                >
                  {request.status}
                </StatusPill>
              </p>
              <p>Payment reference: {request.payment_reference}</p>
              <small>Submitted {formatRecordDate(request.created_at, "en", true)}</small>
              {request.review_note && <p>Admin response: {request.review_note}</p>}
              {admin && request.status === "pending" && (
                <div className="taxi-subscription-actions">
                  {(["approved", "rejected"] as const).map((decision) => (
                    <button
                      key={decision}
                      className={`button button--${decision === "approved" ? "primary" : "secondary"}`}
                      onClick={() => {
                        setReview({ request, decision });
                        setMessage("");
                        dialog.current?.showModal();
                      }}
                    >
                      {decision === "approved" ? "Approve" : "Reject"}
                    </button>
                  ))}
                </div>
              )}
            </DetailCard>
          )}
        />
      </div>
      <dialog
        ref={dialog}
        className="customer-order-filter-dialog"
        aria-labelledby="subscription-review-title"
        onCancel={(event) => {
          if (busy) event.preventDefault();
        }}
      >
        <form key={review?.request.id + String(review?.decision)} onSubmit={decide}>
          <header>
            <h2 id="subscription-review-title">
              {review?.decision === "approved"
                ? "Approve subscription fee"
                : "Reject subscription fee"}
            </h2>
            <button
              type="button"
              className="button button--secondary"
              aria-label="Close"
              disabled={busy}
              onClick={() => dialog.current?.close()}
            >
              ×
            </button>
          </header>
          <p>
            {review && money(review.request.fee_minor)} · {review?.request.billing_cycle}
          </p>
          <p>Payment reference: {review?.request.payment_reference}</p>
          {review?.decision === "approved" && (
            <label>
              <input type="checkbox" required /> I have verified this payment.
            </label>
          )}
          <label>
            {review?.decision === "rejected" ? "Rejection reason" : "Admin note (optional)"}
            <textarea name="note" maxLength={1000} required={review?.decision === "rejected"} />
          </label>
          {review && message && <p role="alert">{message}</p>}
          <button className="button button--primary" disabled={busy}>
            {busy
              ? "Saving…"
              : review?.decision === "approved"
                ? "Confirm approval"
                : "Confirm rejection"}
          </button>
        </form>
      </dialog>
    </section>
  );
}
